import React, { useCallback, useEffect, useState } from "react";
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import SelectOptions from "../SelectOptions";
import { Field } from "formik";
import { InputField } from "../Inputs";
import eligibilityCheckServices from "../../../services/api/service/eligibilityCheckService";
import { setStatusPopup } from "../../../services/redux/slices/statusPopupSlice";
import { useDispatch } from "react-redux";
import locationIcon from "./../../../assets/svgIcons/live-view.svg";

interface Props {
  addressFieldNames: any;
  values: any;
  setFieldValue: (val1: any, val2: any) => void;
  nextCoApplicantNumber: number;
  submitCount: number;
  errors: any;
  touched: any;
  handleInvokPinCodeApi: (
    val1: any,
    val2: any,
    val3: any,
    val4: string,
    val5: string,
    val6: boolean,
  ) => void;
  onZipCodeLoadingChange?: (loading: boolean) => void;
}

const AddressDetailsTab = ({
  addressFieldNames,
  values,
  setFieldValue,
  nextCoApplicantNumber,
  submitCount,
  errors,
  touched,
  handleInvokPinCodeApi,
  onZipCodeLoadingChange,
}: Props) => {
  const dispatch = useDispatch();
  const [zipCodeLoading, setZipCodeLoading] = useState<boolean>(false);

  // Surface this tab's own pincode-lookup loading state to the parent
  // modal so its Save button can disable while getZipCode/getPinCode are
  // still in flight.
  useEffect(() => {
    onZipCodeLoadingChange?.(zipCodeLoading);
  }, [zipCodeLoading, onZipCodeLoadingChange]);

  const debouncedZipCodeCall = useCallback(
    async (
      pincode: string,
      coAppNum: number,
      fieldType?: string,
      currentsameAsPermanent?: boolean,
    ) => {
      if (pincode && String(pincode).length === 6) {
        setZipCodeLoading(true);
        try {
          const resp = await eligibilityCheckServices.getZipCode(pincode);
          if (resp.status === 200) {
            if (resp?.data?.data.length > 0) {
              const {
                cityName,
                countryName,
                stateName,
                stateCode,
                pinCodeId,
                city,
              }: any = resp?.data?.data?.[0];

              await handleInvokPinCodeApi(
                pincode,
                fieldType,
                stateCode,
                pinCodeId,
                city,
                currentsameAsPermanent ? true : false,
              );

              // Determine if this is a permanent address field
              const isPermanentField = fieldType === "permanent";
              const isCurrentField = fieldType === "current";

              // Safety check - if fieldType is not specified, don't update any fields to avoid confusion
              if (!fieldType) {
                console.warn(
                  "handleInvokZipCode called without fieldType, skipping field updates",
                );
                return;
              }

              if (coAppNum) {
                const sameAsKey = `addressCoApplicant${coAppNum}currentsameAsPermanent`;
                const isSameAs = values?.[sameAsKey];

                if (isPermanentField) {
                  // Update permanent fields when permanent pincode is entered
                  setFieldValue(
                    `addressCoApplicant${coAppNum}permanentCity`,
                    cityName || "",
                  );
                  setFieldValue(
                    `addressCoApplicant${coAppNum}permanentState`,
                    stateName || "",
                  );
                  setFieldValue(
                    `addressCoApplicant${coAppNum}permanentCountry`,
                    countryName || "",
                  );
                  // Only update current fields if same as permanent is checked
                  if (isSameAs) {
                    setFieldValue(
                      `addressCoApplicant${coAppNum}currentCity`,
                      cityName || "",
                    );
                    setFieldValue(
                      `addressCoApplicant${coAppNum}currentState`,
                      stateName || "",
                    );
                    setFieldValue(
                      `addressCoApplicant${coAppNum}currentCountry`,
                      countryName || "",
                    );
                  }
                } else if (isCurrentField) {
                  // Update current fields when current pincode is entered
                  setFieldValue(
                    `addressCoApplicant${coAppNum}currentCity`,
                    cityName || "",
                  );
                  setFieldValue(
                    `addressCoApplicant${coAppNum}currentState`,
                    stateName || "",
                  );
                  setFieldValue(
                    `addressCoApplicant${coAppNum}currentCountry`,
                    countryName || "",
                  );
                  // Only update permanent fields if same as current is checked
                  if (isSameAs) {
                    setFieldValue(
                      `addressCoApplicant${coAppNum}permanentCity`,
                      cityName || "",
                    );
                    setFieldValue(
                      `addressCoApplicant${coAppNum}permanentState`,
                      stateName || "",
                    );
                    setFieldValue(
                      `addressCoApplicant${coAppNum}permanentCountry`,
                      countryName || "",
                    );
                  }
                }
              }
            } else {
              const isPermanentField = fieldType === "permanent";
              const isCurrentField = fieldType === "current";

              if (coAppNum) {
                if (isPermanentField) {
                  setFieldValue(
                    `addressCoApplicant${coAppNum}permanentCity`,
                    "",
                  );
                  setFieldValue(
                    `addressCoApplicant${coAppNum}permanentState`,
                    "",
                  );
                } else if (isCurrentField) {
                  setFieldValue(`addressCoApplicant${coAppNum}currentCity`, "");
                  setFieldValue(
                    `addressCoApplicant${coAppNum}currentState`,
                    "",
                  );
                }
              }

              dispatch(
                setStatusPopup({
                  status: "error",
                  message: "Pincode not found. Please enter a valid pincode.",
                }),
              );
            }
          }
        } catch (error: any) {
          console.log("error", error);
        } finally {
          setZipCodeLoading(false);
        }
      }
    },
    [dispatch, values],
  );

  const handleInvokZipCode = (
    pincode: string,
    coAppNum: number,
    fieldType?: string,
    currentsameAsPermanent?: boolean,
  ) => {
    // Only call API if pincode is exactly 6 digits
    if (pincode && pincode.length === 6) {
      debouncedZipCodeCall(
        pincode,
        coAppNum,
        fieldType,
        currentsameAsPermanent ? true : false,
      );
    }
  };

  const getFieldError = (fieldName: string) =>
    Boolean(
      (submitCount > 0 && (errors as any)[fieldName]) ||
      ((touched as any)[fieldName] && (errors as any)[fieldName]),
    );

  const getFieldHelperText = (fieldName: string) =>
    getFieldError(fieldName) ? String((errors as any)[fieldName]) : "";

  useEffect(() => {
    if (!values[addressFieldNames.currentsameAsPermanent]) {
      return;
    }

    setFieldValue(
      addressFieldNames.currentAddressLine1,
      values[addressFieldNames.permanentAddressLine1] || "",
    );
    setFieldValue(
      addressFieldNames.currentAddressLine2,
      values[addressFieldNames.permanentAddressLine2] || "",
    );
    setFieldValue(
      addressFieldNames.currentAddressLine3,
      values[addressFieldNames.permanentAddressLine3] || "",
    );
    setFieldValue(
      addressFieldNames.currentPincode,
      values[addressFieldNames.permanentPincode] || "",
    );
    setFieldValue(
      addressFieldNames.currentCity,
      values[addressFieldNames.permanentCity] || "",
    );
    setFieldValue(
      addressFieldNames.currentState,
      values[addressFieldNames.permanentState] || "",
    );
    setFieldValue(
      addressFieldNames.currentCountry,
      values[addressFieldNames.permanentCountry] || "",
    );
    setFieldValue(
      addressFieldNames.currentOwnershipStatus,
      values[addressFieldNames.permanentOwnershipStatus] || "",
    );
    setFieldValue(
      addressFieldNames.currentYearsInCurrentAddress,
      values[addressFieldNames.permanentYearsInCurrentAddress] || "",
    );
    setFieldValue(
      addressFieldNames.currentYearsInCurrentCity,
      values[addressFieldNames.permanentYearsInCurrentCity] || "",
    );
  }, [
    values[addressFieldNames.currentsameAsPermanent],
    values[addressFieldNames.permanentAddressLine1],
    values[addressFieldNames.permanentAddressLine2],
    values[addressFieldNames.permanentAddressLine3],
    values[addressFieldNames.permanentPincode],
    values[addressFieldNames.permanentCity],
    values[addressFieldNames.permanentState],
    values[addressFieldNames.permanentCountry],
    values[addressFieldNames.permanentOwnershipStatus],
    values[addressFieldNames.permanentYearsInCurrentAddress],
    values[addressFieldNames.permanentYearsInCurrentCity],
    addressFieldNames,
    setFieldValue,
  ]);

  return (
    <Box sx={{ px: { xs: 0, sm: 1 }, pb: 1, mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: { xs: 0, sm: 1 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: "8px",
            bgcolor: "#e5feffff",
          }}
        >
          <img src={locationIcon} alt="Company Address Icon" />
        </Box>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "14px",
            color: "#616161",
          }}
        >
          Permanent Address
        </Typography>
      </Box>
      <Grid container spacing={2} sx={{ px: { xs: 0, sm: 1 }, pb: 1 }}>
        <Grid item xs={12} sm={4}>
          <Box>
            <label className="text-lable" style={{ display: "block" }}>
              Ownership Status <span style={{ color: "red" }}>*</span>
            </label>
            <SelectOptions
              label=""
              value={
                (values as any)[
                  addressFieldNames.permanentOwnershipStatus
                ] as string
              }
              options={[
                { value: "Rented", label: "Rented" },
                { value: "Owned", label: "Owned" },
              ]}
              isMandatory={false}
              placeholder="Select ownership status"
              onChange={(val) =>
                setFieldValue(addressFieldNames.permanentOwnershipStatus, val)
              }
              error={getFieldError(addressFieldNames.permanentOwnershipStatus)}
              helperText={getFieldHelperText(
                addressFieldNames.permanentOwnershipStatus,
              )}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <label className="text-lable" style={{ display: "block" }}>
              How long have you been living here?{" "}
              <span style={{ color: "red" }}>*</span>
            </label>
            <Field name={addressFieldNames.permanentYearsInCurrentAddress}>
              {({ field: f, meta, form }: any) => (
                <InputField
                  {...f}
                  fullWidth
                  placeholder="How long have you been living here (in years)"
                  variant="outlined"
                  type="number"
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    autoComplete: "off",
                  }}
                  value={
                    (values as any)[
                      addressFieldNames.permanentYearsInCurrentAddress
                    ] || ""
                  }
                  onChange={(e: any) =>
                    setFieldValue(
                      addressFieldNames.permanentYearsInCurrentAddress,
                      e.target.value,
                    )
                  }
                  error={
                    (submitCount > 0 &&
                      !!(errors as any)[
                        addressFieldNames.permanentYearsInCurrentAddress
                      ]) ||
                    (!!(touched as any)[
                      addressFieldNames.permanentYearsInCurrentAddress
                    ] &&
                      !!(errors as any)[
                        addressFieldNames.permanentYearsInCurrentAddress
                      ])
                  }
                  helperText={
                    (submitCount > 0 ||
                      (touched as any)[
                        addressFieldNames.permanentYearsInCurrentAddress
                      ]) &&
                    (errors as any)[
                      addressFieldNames.permanentYearsInCurrentAddress
                    ]
                      ? String(
                          (errors as any)[
                            addressFieldNames.permanentYearsInCurrentAddress
                          ],
                        )
                      : ""
                  }
                />
              )}
            </Field>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <label className="text-lable" style={{ display: "block" }}>
              How long have you lived in this city
              <span style={{ color: "red" }}>*</span>
            </label>
            <Field name={addressFieldNames.permanentYearsInCurrentCity}>
              {({ field: f }: any) => (
                <InputField
                  {...f}
                  fullWidth
                  placeholder="How long have you lived in this city (in years)"
                  variant="outlined"
                  type="number"
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    autoComplete: "off",
                  }}
                  value={
                    (values as any)[
                      addressFieldNames.permanentYearsInCurrentCity
                    ] || ""
                  }
                  onChange={(e: any) =>
                    setFieldValue(
                      addressFieldNames.permanentYearsInCurrentCity,
                      e.target.value,
                    )
                  }
                  error={
                    (submitCount > 0 &&
                      !!(errors as any)[
                        addressFieldNames.permanentYearsInCurrentCity
                      ]) ||
                    (!!(touched as any)[
                      addressFieldNames.permanentYearsInCurrentCity
                    ] &&
                      !!(errors as any)[
                        addressFieldNames.permanentYearsInCurrentCity
                      ])
                  }
                  helperText={
                    (submitCount > 0 ||
                      (touched as any)[
                        addressFieldNames.permanentYearsInCurrentCity
                      ]) &&
                    (errors as any)[
                      addressFieldNames.permanentYearsInCurrentCity
                    ]
                      ? String(
                          (errors as any)[
                            addressFieldNames.permanentYearsInCurrentCity
                          ],
                        )
                      : ""
                  }
                />
              )}
            </Field>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <label className="text-lable" style={{ display: "block" }}>
              Address Line 1<span style={{ color: "red" }}>*</span>
            </label>
            <InputField
              fullWidth
              placeholder="Enter Address Line 1"
              value={
                (values as any)[addressFieldNames.permanentAddressLine1] || ""
              }
              onChange={(e: any) =>
                setFieldValue(
                  addressFieldNames.permanentAddressLine1,
                  e.target.value,
                )
              }
              error={getFieldError(addressFieldNames.permanentAddressLine1)}
              helperText={getFieldHelperText(
                addressFieldNames.permanentAddressLine1,
              )}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <label className="text-lable" style={{ display: "block" }}>
              Address Line 2
            </label>
            <InputField
              fullWidth
              placeholder="Enter Address Line 2"
              value={
                (values as any)[addressFieldNames.permanentAddressLine2] || ""
              }
              onChange={(e: any) =>
                setFieldValue(
                  addressFieldNames.permanentAddressLine2,
                  e.target.value,
                )
              }
              error={getFieldError(addressFieldNames.permanentAddressLine2)}
              helperText={getFieldHelperText(
                addressFieldNames.permanentAddressLine2,
              )}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <label className="text-lable" style={{ display: "block" }}>
              Address Line 3
            </label>
            <InputField
              fullWidth
              placeholder="Enter Address Line 3"
              value={
                (values as any)[addressFieldNames.permanentAddressLine3] || ""
              }
              onChange={(e: any) =>
                setFieldValue(
                  addressFieldNames.permanentAddressLine3,
                  e.target.value,
                )
              }
              error={getFieldError(addressFieldNames.permanentAddressLine3)}
              helperText={getFieldHelperText(
                addressFieldNames.permanentAddressLine3,
              )}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <label className="text-lable" style={{ display: "block" }}>
              Pincode<span style={{ color: "red" }}>*</span>
            </label>
            <InputField
              fullWidth
              placeholder="Enter Pincode"
              inputProps={{
                maxLength: 6,
                inputMode: "numeric",
                pattern: "[0-9]*",
                autoComplete: "off",
              }}
              value={(values as any)[addressFieldNames.permanentPincode] || ""}
              onChange={(e: any) => {
                setFieldValue(
                  addressFieldNames.permanentPincode,
                  e.target.value,
                );
                handleInvokZipCode(
                  e.target.value,
                  nextCoApplicantNumber,
                  "permanent",
                  (values as any)[addressFieldNames.currentsameAsPermanent],
                );
              }}
              error={getFieldError(addressFieldNames.permanentPincode)}
              helperText={
                zipCodeLoading
                  ? "Validating pincode..."
                  : getFieldHelperText(addressFieldNames.permanentPincode)
              }
              InputProps={{
                endAdornment: zipCodeLoading ? (
                  <CircularProgress size={20} />
                ) : undefined,
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <label className="text-lable" style={{ display: "block" }}>
              City <span style={{ color: "red" }}>*</span>
            </label>
            <SelectOptions
              label=""
              value={(values as any)[addressFieldNames.permanentCity] as string}
              options={[]}
              disabled
              isMandatory={true}
              placeholder="Select City"
              onChange={(val) =>
                setFieldValue(addressFieldNames.permanentCity, val)
              }
              error={getFieldError(addressFieldNames.permanentCity)}
              helperText={getFieldHelperText(addressFieldNames.permanentCity)}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <label className="text-lable" style={{ display: "block" }}>
              State <span style={{ color: "red" }}>*</span>
            </label>
            <SelectOptions
              label=""
              value={
                (values as any)[addressFieldNames.permanentState] as string
              }
              options={[]}
              disabled
              isMandatory={true}
              placeholder="Select State"
              onChange={(val) =>
                setFieldValue(addressFieldNames.permanentState, val)
              }
              error={getFieldError(addressFieldNames.permanentState)}
              helperText={getFieldHelperText(addressFieldNames.permanentState)}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <label className="text-lable" style={{ display: "block" }}>
              Country <span style={{ color: "red" }}>*</span>
            </label>
            <SelectOptions
              label=""
              value={
                (values as any)[addressFieldNames.permanentCountry] as string
              }
              options={[]}
              disabled
              isMandatory={true}
              placeholder="Select Country"
              onChange={(val) =>
                setFieldValue(addressFieldNames.permanentCountry, val)
              }
              error={getFieldError(addressFieldNames.permanentCountry)}
              helperText={getFieldHelperText(
                addressFieldNames.permanentCountry,
              )}
            />
          </Box>
        </Grid>
      </Grid>
      <Box mt={3} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: "8px",
            bgcolor: "#e5feffff",
          }}
        >
          <img src={locationIcon} alt="Company Address Icon" />
        </Box>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "14px",
            color: "#616161",
            px: { xs: 0, sm: 1 },
          }}
        >
          Current Address
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ px: { xs: 0, sm: 1 }, pb: 1 }}>
        <Grid item xs={12} sm={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(
                  (values as any)[addressFieldNames.currentsameAsPermanent],
                )}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFieldValue(
                    addressFieldNames.currentsameAsPermanent,
                    checked,
                  );
                  if (checked) {
                    // copy permanent to current
                    setFieldValue(
                      addressFieldNames.currentAddressLine1,
                      (values as any)[
                        addressFieldNames.permanentAddressLine1
                      ] || "",
                    );
                    setFieldValue(
                      addressFieldNames.currentAddressLine2,
                      (values as any)[
                        addressFieldNames.permanentAddressLine2
                      ] || "",
                    );
                    setFieldValue(
                      addressFieldNames.currentAddressLine3,
                      (values as any)[
                        addressFieldNames.permanentAddressLine3
                      ] || "",
                    );
                    setFieldValue(
                      addressFieldNames.currentPincode,
                      (values as any)[addressFieldNames.permanentPincode] || "",
                    );
                    setFieldValue(
                      addressFieldNames.currentCity,
                      (values as any)[addressFieldNames.permanentCity] || "",
                    );
                    setFieldValue(
                      addressFieldNames.currentState,
                      (values as any)[addressFieldNames.permanentState] || "",
                    );
                    setFieldValue(
                      addressFieldNames.currentCountry,
                      (values as any)[addressFieldNames.permanentCountry] || "",
                    );
                    setFieldValue(
                      addressFieldNames.currentOwnershipStatus,
                      (values as any)[
                        addressFieldNames.permanentOwnershipStatus
                      ] || "",
                    );
                    setFieldValue(
                      addressFieldNames.currentYearsInCurrentAddress,
                      (values as any)[
                        addressFieldNames.permanentYearsInCurrentAddress
                      ] || "",
                    );
                    setFieldValue(
                      addressFieldNames.currentYearsInCurrentCity,
                      (values as any)[
                        addressFieldNames.permanentYearsInCurrentCity
                      ] || "",
                    );
                  }
                }}
              />
            }
            label={
              <span className="text-lable">
                Keep current address same as permanent address
              </span>
            }
          />
        </Grid>

        <>
          <Grid item xs={12} sm={4}>
            <Box>
              <label className="text-lable" style={{ display: "block" }}>
                Ownership Status <span style={{ color: "red" }}>*</span>
              </label>
              <SelectOptions
                label=""
                value={
                  (values as any)[
                    addressFieldNames.currentOwnershipStatus
                  ] as string
                }
                options={[
                  { value: "Rented", label: "Rented" },
                  { value: "Owned", label: "Owned" },
                ]}
                isMandatory={false}
                placeholder="Select ownership status"
                onChange={(val) =>
                  setFieldValue(addressFieldNames.currentOwnershipStatus, val)
                }
                error={getFieldError(addressFieldNames.currentOwnershipStatus)}
                helperText={getFieldHelperText(
                  addressFieldNames.currentOwnershipStatus,
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box>
              <label className="text-lable" style={{ display: "block" }}>
                How long have you been living here?{" "}
                <span style={{ color: "red" }}>*</span>
              </label>
              <Field name={addressFieldNames.currentYearsInCurrentAddress}>
                {({ field: f, meta, form }: any) => (
                  <InputField
                    {...f}
                    fullWidth
                    placeholder="How long have you been living here (in years)"
                    variant="outlined"
                    type="number"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      autoComplete: "off",
                    }}
                    value={
                      (values as any)[
                        addressFieldNames.currentYearsInCurrentAddress
                      ] || ""
                    }
                    onChange={(e: any) =>
                      setFieldValue(
                        addressFieldNames.currentYearsInCurrentAddress,
                        e.target.value,
                      )
                    }
                    error={getFieldError(
                      addressFieldNames.currentYearsInCurrentAddress,
                    )}
                    helperText={getFieldHelperText(
                      addressFieldNames.currentYearsInCurrentAddress,
                    )}
                  />
                )}
              </Field>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box>
              <label className="text-lable" style={{ display: "block" }}>
                How long have you lived in this city
                <span style={{ color: "red" }}>*</span>
              </label>
              <Field name={addressFieldNames.currentYearsInCurrentCity}>
                {({ field: f }: any) => (
                  <InputField
                    {...f}
                    fullWidth
                    placeholder="How long have you lived in this city (in years)"
                    variant="outlined"
                    type="number"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      autoComplete: "off",
                    }}
                    value={
                      (values as any)[
                        addressFieldNames.currentYearsInCurrentCity
                      ] || ""
                    }
                    onChange={(e: any) =>
                      setFieldValue(
                        addressFieldNames.currentYearsInCurrentCity,
                        e.target.value,
                      )
                    }
                    error={getFieldError(
                      addressFieldNames.currentYearsInCurrentCity,
                    )}
                    helperText={getFieldHelperText(
                      addressFieldNames.currentYearsInCurrentCity,
                    )}
                  />
                )}
              </Field>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box>
              <label className="text-lable" style={{ display: "block" }}>
                Address Line 1<span style={{ color: "red" }}>*</span>
              </label>
              <InputField
                fullWidth
                placeholder="Enter Address Line 1"
                value={
                  (values as any)[addressFieldNames.currentAddressLine1] || ""
                }
                onChange={(e: any) =>
                  setFieldValue(
                    addressFieldNames.currentAddressLine1,
                    e.target.value,
                  )
                }
                error={getFieldError(addressFieldNames.currentAddressLine1)}
                helperText={getFieldHelperText(
                  addressFieldNames.currentAddressLine1,
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box>
              <label className="text-lable" style={{ display: "block" }}>
                Address Line 2
              </label>
              <InputField
                fullWidth
                placeholder="Enter Address Line 2"
                value={
                  (values as any)[addressFieldNames.currentAddressLine2] || ""
                }
                onChange={(e: any) =>
                  setFieldValue(
                    addressFieldNames.currentAddressLine2,
                    e.target.value,
                  )
                }
                error={getFieldError(addressFieldNames.currentAddressLine2)}
                helperText={getFieldHelperText(
                  addressFieldNames.currentAddressLine2,
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box>
              <label className="text-lable" style={{ display: "block" }}>
                Address Line 3
              </label>
              <InputField
                fullWidth
                placeholder="Enter Address Line 3"
                value={
                  (values as any)[addressFieldNames.currentAddressLine3] || ""
                }
                onChange={(e: any) =>
                  setFieldValue(
                    addressFieldNames.currentAddressLine3,
                    e.target.value,
                  )
                }
                error={getFieldError(addressFieldNames.currentAddressLine3)}
                helperText={getFieldHelperText(
                  addressFieldNames.currentAddressLine3,
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box>
              <label className="text-lable" style={{ display: "block" }}>
                Pincode<span style={{ color: "red" }}>*</span>
              </label>
              <InputField
                fullWidth
                placeholder="Enter Pincode"
                inputProps={{
                  maxLength: 6,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  autoComplete: "off",
                }}
                value={(values as any)[addressFieldNames.currentPincode] || ""}
                onChange={(e: any) => {
                  setFieldValue(
                    addressFieldNames.currentPincode,
                    e.target.value,
                  );
                  handleInvokZipCode(
                    e.target.value,
                    nextCoApplicantNumber,
                    "current",
                    (values as any)[addressFieldNames.currentsameAsPermanent],
                  );
                }}
                error={getFieldError(addressFieldNames.currentPincode)}
                helperText={
                  zipCodeLoading
                    ? "Validating pincode..."
                    : getFieldHelperText(addressFieldNames.currentPincode)
                }
                InputProps={{
                  endAdornment: zipCodeLoading ? (
                    <CircularProgress size={20} />
                  ) : undefined,
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box>
              <label className="text-lable" style={{ display: "block" }}>
                City <span style={{ color: "red" }}>*</span>
              </label>
              <SelectOptions
                label=""
                value={(values as any)[addressFieldNames.currentCity] as string}
                options={[]}
                isMandatory={true}
                disabled
                placeholder="Select City"
                onChange={(val) =>
                  setFieldValue(addressFieldNames.currentCity, val)
                }
                error={getFieldError(addressFieldNames.currentCity)}
                helperText={getFieldHelperText(addressFieldNames.currentCity)}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box>
              <label className="text-lable" style={{ display: "block" }}>
                State <span style={{ color: "red" }}>*</span>
              </label>
              <SelectOptions
                label=""
                value={
                  (values as any)[addressFieldNames.currentState] as string
                }
                options={[]}
                disabled
                isMandatory={true}
                placeholder="Select State"
                onChange={(val) =>
                  setFieldValue(addressFieldNames.currentState, val)
                }
                error={getFieldError(addressFieldNames.currentState)}
                helperText={getFieldHelperText(addressFieldNames.currentState)}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box>
              <label className="text-lable" style={{ display: "block" }}>
                Country <span style={{ color: "red" }}>*</span>
              </label>
              <SelectOptions
                label=""
                value={
                  (values as any)[addressFieldNames.currentCountry] as string
                }
                options={[]}
                disabled
                isMandatory={true}
                placeholder="Select Country"
                onChange={(val) =>
                  setFieldValue(addressFieldNames.currentCountry, val)
                }
                error={getFieldError(addressFieldNames.currentCountry)}
                helperText={getFieldHelperText(
                  addressFieldNames.currentCountry,
                )}
              />
            </Box>
          </Grid>
        </>
      </Grid>
    </Box>
  );
};

export default AddressDetailsTab;